import { motion, useScroll, useTransform } from "framer-motion";
import { Play, Radio } from "lucide-react";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: 0, transition: { duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] } }),
};

const MaskedLine = ({ children, i, className }) => (
  <div className="overflow-hidden">
    <motion.div custom={i} variants={line} initial="hidden" animate="show" className={className}>
      {children}
    </motion.div>
  </div>
);

export const Hero = ({ trackCount }) => {
  const { scrollY } = useScroll();
  const glowY = useTransform(scrollY, [0, 600], [0, 180]);
  const titleY = useTransform(scrollY, [0, 600], [0, -80]);

  return (
    <section id="top" className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-24 pb-32">
      <motion.div style={{ y: glowY }} className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 left-1/4 w-[520px] h-[520px] rounded-full bg-[#00f0ff]/10 blur-[140px]" />
        <div className="absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full bg-[#ff0055]/10 blur-[140px]" />
        <div className="absolute top-1/3 right-1/4 w-[260px] h-[260px] rounded-full bg-[#00ff66]/5 blur-[120px]" />
      </motion.div>

      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)", backgroundSize: "64px 64px" }} />

      <motion.div style={{ y: titleY }} className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full">
        <MaskedLine i={0} className="font-mono-tech text-xs sm:text-sm tracking-[0.4em] text-[#00f0ff] uppercase mb-6 flex items-center gap-3">
          <span className="inline-block w-2 h-2 bg-[#00ff66] rounded-full pulse-ring" />
          Independent Record Label — EST. 2026 — r-an.app
        </MaskedLine>

        <h1 className="font-display font-black uppercase leading-[0.95] text-4xl sm:text-5xl lg:text-6xl text-white">
          <MaskedLine i={1}>The Frequency</MaskedLine>
          <MaskedLine i={2}>
            <span className="text-transparent" style={{ WebkitTextStroke: "2px #00f0ff" }}>of the</span>
          </MaskedLine>
          <MaskedLine i={3}>
            <span className="neon-text-magenta text-[#ff0055]">Underground</span>
          </MaskedLine>
        </h1>

        <MaskedLine i={4} className="mt-8 max-w-xl text-base text-slate-400 leading-relaxed">
          R-AN releases uncompromising electronic music. Stream the first minute of every production, follow the links to every platform, and dive into our full-catalogue playlists.
        </MaskedLine>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.7 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-listen"
            onClick={() => document.getElementById("releases")?.scrollIntoView({ behavior: "smooth" })}
            className="group flex items-center gap-3 px-7 py-4 bg-[#00f0ff] text-black font-mono-tech text-xs tracking-[0.2em] uppercase font-bold hover:bg-white transition-colors"
          >
            <Play size={14} fill="currentColor" /> Listen to Releases
          </button>
          <button
            data-testid="hero-cta-demo"
            onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
            className="flex items-center gap-3 px-7 py-4 border border-white/20 text-white font-mono-tech text-xs tracking-[0.2em] uppercase hover:border-[#ff0055] hover:text-[#ff0055] transition-colors"
          >
            <Radio size={14} /> Submit Your Demo
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-16 grid grid-cols-3 max-w-md gap-px bg-white/10 border border-white/10"
        >
          {[
            { v: String(trackCount || 6).padStart(2, "0"), l: "Releases" },
            { v: "05", l: "Platforms" },
            { v: "60s", l: "Free Preview" },
          ].map((s) => (
            <div key={s.l} className="bg-[#08080c] px-4 py-5">
              <div className="font-display text-2xl font-bold text-[#00f0ff]">{s.v}</div>
              <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-slate-500 mt-1">{s.l}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <div className="absolute bottom-0 inset-x-0 border-t border-white/10 bg-[#0a0a10] py-3 overflow-hidden">
        <div className="marquee-track font-mono-tech text-[11px] tracking-[0.35em] uppercase text-slate-500">
          {[...Array(2)].map((_, k) => (
            <span key={k} className="flex shrink-0">
              {["Industrial Techno", "Acid", "Hypnotic", "Hard Groove", "Melodic", "Ambient"].map((g) => (
                <span key={g} className="flex items-center">
                  <span className="px-6">{g}</span>
                  <span className="text-[#ff0055]">●</span>
                </span>
              ))}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};
